import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import { useCreateArtistDspConnectionMutation } from 'api/artistProfile';
import { DSP_API, DSP_URL_PARSER } from 'api/digitalServiceProviders';
import {
  DIGITAL_SERVICE_PROVIDERS,
  DSP_NAMES_FORMATTED,
  RELEASE_CONFIGS
} from 'config/constants';
import ENTITY_PROPTYPES from 'config/constants/propTypes';
import { showAlert } from 'redux/actions/ui';
import { makeSelectIsUserArtistMember, selectIsAdminUser } from 'redux/selectors';
import { generateSelectorsFromTestIds } from 'helpers/utilities';

import ArtistSummaryRow from 'components/ArtistSummaryRow';
import Card from 'components/Card';
import WizardStep from 'components/Wizard/WizardStep';
import WizardSearchCard from 'components/Wizard/WizardSearchCard';

import { DspProfileRow } from './DspProfileRow';
import DspConnectionConditionalCard from './DspConnectionConditionalCard';
import { useCreateReleaseConfigMutation } from 'api/release';
import { getDspConnectionSubtitle } from './getDspConnectionSubtitle';

import { canUserCreateNewDspProfile } from './helpers';
import { NameUpdateModal } from './NameUpdateModal';

const { SOUNDCLOUD, APPLE_MUSIC, SPOTIFY } = DIGITAL_SERVICE_PROVIDERS;

const DspConnection = ({
  artistProfile,
  // wizard props:
  service, // see DIGITAL_SERVICE_PROVIDERS constant
  handlePrevious,
  handleContinue,
  ...wizardProps
}) => {
  const { context } = wizardProps;
  const [isConnecting, setIsConnecting] = useState(false);
  const dispatch = useDispatch();
  const artistConnection = artistProfile?.connections?.find(({ dsp }) => dsp === service);
  const serviceName = DSP_NAMES_FORMATTED[service];
  const artistName = artistProfile?.name;
  const artistId = artistProfile?.id;

  //  -- handle label-release context where releaseId is passed --
  const { search } = useLocation();
  const { releaseId } = qs.parse(search);
  const [createReleaseConfig] = useCreateReleaseConfigMutation();
  const [createArtistDspConnection] = useCreateArtistDspConnectionMutation();

  // -- component state --
  const [dspProfiles, setDspProfiles] = useState([]);
  const [searchInputText, setSearchInputText] = useState(artistProfile?.name || '');
  const [isFetchingDspProfile, setIsFetchingDspProfiles] = useState(false);
  const [searchQuery] = useDebounce(searchInputText, 500);
  const [selectedProfileId, setSelectedProfileId] = useState(() => {
    if (
      service === SPOTIFY &&
      artistProfile?.spotify_profile_creation_pending &&
      !artistConnection
    ) {
      return DspConnection.VALUES.NEW_ARTIST_SELECTION;
    }
    return null;
  });
  const [showNameUpdateModal, setShowNameUpdateModal] = useState(false);

  // -- redux select variables --
  const isAdminUser = useSelector(selectIsAdminUser);
  const selectIsUserArtistMember = useMemo(() => makeSelectIsUserArtistMember(), []);
  const isArtistMember = useSelector(state =>
    selectIsUserArtistMember(state, { artistId })
  );

  const searchForProfiles = useCallback(
    searchText => {
      if (!searchText) {
        setDspProfiles([]);
        setIsFetchingDspProfiles(false);
        return Promise.resolve();
      }

      const validUrlLookup = DSP_URL_PARSER[service](searchText);
      const fetchProfiles = validUrlLookup
        ? DSP_API[service].searchProfilesByUrl
        : DSP_API[service].searchProfilesByName;

      if (!fetchProfiles) {
        console.warn(`No api defined for service ${service}`);
        return Promise.resolve();
      }

      setIsFetchingDspProfiles(true);

      return fetchProfiles(searchText)
        .then(({ data }) => {
          setDspProfiles(data);
        })
        .catch(error => {
          setDspProfiles([]);
          dispatch(showAlert({ error }));
        })
        .finally(() => setIsFetchingDspProfiles(false));
    },
    [service, dispatch]
  );

  useEffect(() => {
    searchForProfiles(searchQuery);
  }, [searchQuery, searchForProfiles]);

  const proceedToConnect = async (dspProfile, artistProfile) => {
    setIsConnecting(true);

    try {
      await createArtistDspConnection({ service, artistProfile, dspProfile }).unwrap();
    } catch (error) {
      if (error.message === 'SoundCloud profile is already connected to another artist') {
        const connected_proton_artist_id = error.error?.connected_proton_artist_id;

        dispatch(
          showAlert({
            component: 'SoundCloudProfileConnectError',
            componentProps: {
              artist: {
                id: artistProfile.id,
                name: artistProfile.name
              },
              connected_proton_artist_id
            },
            dismissable: true
          })
        );

        throw error;
      }

      dispatch(showAlert({ error }));
      // NOTE: we rethrow the error for `handleContinue` to intercept
      throw error;
    }
    setIsConnecting(false);
    return artistProfile;
  };

  // Associate DSP profile with artist profile
  const onDspContinue = async () => {
    if (
      // For now, if DSP is saved in DB, restrict updates.
      artistConnection ||
      selectedProfileId === DspConnection.VALUES.SKIP_ARTIST_SELECTION
    )
      return artistProfile;

    // For now the new artist selection option will only show for spotify and sometimes apple music.
    if (selectedProfileId === DspConnection.VALUES.NEW_ARTIST_SELECTION) {
      // We don't actually set a release_config if apple music since we automatically create an apple profile
      // if there isn't one when we go to deliver a release.
      if (service === APPLE_MUSIC) return artistProfile;

      setIsConnecting(true);

      try {
        await createReleaseConfig({
          id: releaseId,
          config_key: RELEASE_CONFIGS.CREATE_NEW_SPOTIFY_PROFILE,
          config_value: artistId
        }).unwrap();
      } catch (error) {
        dispatch(showAlert({ error }));
        // NOTE: we rethrow the error for `handleContinue` to intercept
        throw error;
      }
      setIsConnecting(false);
      return artistProfile;
    }

    const dspProfile = dspProfiles.find(({ dsp_id }) => dsp_id === selectedProfileId);
    // If names match or this is SoundCloud or artist is connected to another canonical
    // DSP (spotify/apple music), make the connection, otherwise confirm name
    if (
      dspProfile?.value === artistProfile.name ||
      dspProfile.dsp === 'soundcloud' ||
      artistProfile?.connections.some(dspProfile => dspProfile.dsp !== 'soundcloud')
    ) {
      return proceedToConnect(dspProfile, artistProfile);
    }

    setShowNameUpdateModal(true);
    throw { reason: 'Requires name update or confirmation' };
  };

  const canDisconnectProfile =
    isAdminUser || (artistConnection && isArtistMember && service === SOUNDCLOUD);

  const canCreateDspProfile = canUserCreateNewDspProfile(artistProfile, service, context);

  const subtitle = getDspConnectionSubtitle({
    serviceName,
    artistName,
    artistConnection,
    canDisconnectProfile,
    artistId
  });

  const hasMatchingProfileName = useMemo(
    () =>
      searchQuery === artistProfile?.name &&
      dspProfiles.some(
        profile => profile.value.toLowerCase() === artistProfile.name.toLowerCase()
      ),
    [searchQuery, dspProfiles, artistProfile?.name]
  );

  return (
    <WizardStep
      {...wizardProps}
      title={
        artistConnection
          ? `${serviceName} Profile Connected`
          : `Connect ${serviceName} Profile`
      }
      subtitle={subtitle}
      onContinue={() => handleContinue(onDspContinue)}
      isContinueDisabled={isConnecting || (!artistConnection && !selectedProfileId)}
      onPrevious={handlePrevious}
    >
      <WizardStep.Section title="Artist Info">
        <Card fitHeight shadow={false}>
          <Card.Content>
            <ArtistSummaryRow artist={artistProfile} />
          </Card.Content>
        </Card>
      </WizardStep.Section>

      {!artistConnection && (
        <>
          <WizardStep.Section
            title={`${serviceName} Results`}
            subtitle={`Search for existing ${serviceName} profiles by artist name or their ${serviceName}
          profile URL`}
          >
            <WizardSearchCard
              data={dspProfiles}
              isFetching={isFetchingDspProfile}
              searchText={searchInputText}
              onSearchTextChange={e => {
                const { value } = e.target;
                // We trigger fetching here as well to remove appearance of debounce delay when typing
                if (value) setIsFetchingDspProfiles(true);

                setSearchInputText(value);
              }}
              renderItemTombstone={index => (
                <WizardSearchCard.Item key={index}>
                  <DspProfileRow.Tombstone index={index} />
                </WizardSearchCard.Item>
              )}
              renderItem={({ data }) => (
                <WizardSearchCard.Item
                  key={data.dsp_id || data.id}
                  active={selectedProfileId === data.dsp_id}
                  data-testid={DspConnection.TEST_IDS.ARTIST_RESULT}
                  onClick={() => setSelectedProfileId(data.dsp_id)}
                >
                  <DspProfileRow profile={data} service={service} />
                </WizardSearchCard.Item>
              )}
              noSearchResultsTitle={`No ${serviceName} profile found with that name`}
              noSearchResultsSubtitle={
                canCreateDspProfile
                  ? `Try searching for another name, or click the button below & ` +
                    `we'll help you create a brand new artist profile on ${serviceName}`
                  : null
              }
            />
          </WizardStep.Section>

          <DspConnectionConditionalCard
            artistProfile={artistProfile}
            dspArtistWithMatchingNameExists={hasMatchingProfileName}
            context={context}
            canCreateDspProfile={canCreateDspProfile}
            onSelect={setSelectedProfileId}
            selectedValue={selectedProfileId}
            service={service}
          />
        </>
      )}
      {showNameUpdateModal && (
        <NameUpdateModal
          artistProfile={artistProfile}
          dspProfile={dspProfiles.find(({ dsp_id }) => dsp_id === selectedProfileId)}
          continueToConnection={(dspProfile, artistProfile) =>
            handleContinue(() => proceedToConnect(dspProfile, artistProfile))
          }
          closeModal={() => setShowNameUpdateModal(false)}
          createAlert={options => dispatch(showAlert(options))}
        />
      )}
    </WizardStep>
  );
};

DspConnection.VALUES = {
  NEW_ARTIST_SELECTION: 'newArtist',
  SKIP_ARTIST_SELECTION: 'skip'
};

DspConnection.TEST_IDS = {
  SKIP_DSP_CONNECT: 'DspConnection-skip-connect-card',
  CONTACT_SUPPORT: 'DspConnection-contact-support-card',
  SEARCH_INPUT: WizardSearchCard.TEST_IDS.SEARCH_INPUT,
  ARTIST_RESULT: 'DspConnection-artist-result',
  NEW_PROFILE_SELECT: 'DspConnection-new-profile-select-card'
};

DspConnection.SELECTORS = generateSelectorsFromTestIds(DspConnection.TEST_IDS);

DspConnection.propTypes = {
  artistProfile: ENTITY_PROPTYPES.ARTIST_PROFILE,
  service: ENTITY_PROPTYPES.DSP_TYPES,
  displayedResultCount: PropTypes.number,
  isAdminUser: PropTypes.bool,
  context: PropTypes.string,
  user: ENTITY_PROPTYPES.USER,
  // Wizard props
  handleContinue: PropTypes.func,
  handlePrevious: PropTypes.func
};

export default DspConnection;
